import { useEffect, useMemo, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Textarea } from './ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Checkbox } from './ui/checkbox';
import {
  Upload,
  Download,
  Tag,
  X,
  CheckCircle2,
  AlertTriangle,
  Search,
  RefreshCw,
  Pencil,
  FileText,
  Plus,
  Trash2,
  History as HistoryIcon,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const formatINR = (n) =>
  `₹${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
const formatDate = (iso) =>
  iso
    ? new Date(iso).toLocaleDateString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      })
    : '—';

// Pretty-print an audit-log value for the history drawer. Numbers get the ₹
// treatment for debit/credit/statement_balance; dates render as `dd Mon yyyy`.
const formatAuditValue = (field, v) => {
  if (v == null || v === '') return '—';
  if (field === 'txn_date') return formatDate(v);
  if (['debit', 'credit', 'statement_balance'].includes(field)) {
    return `₹${Number(v || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
  }
  return String(v);
};

const ADJ_CATEGORIES = [
  { v: 'bank_charge', label: 'Bank charge' },
  { v: 'interest', label: 'Interest credit' },
  { v: 'tds', label: 'TDS' },
  { v: 'transfer', label: 'Inter-account transfer' },
  { v: 'other', label: 'Other' },
];

const today = () => new Date().toISOString().split('T')[0];
const daysAgo = (d) => {
  const t = new Date();
  t.setDate(t.getDate() - d);
  return t.toISOString().split('T')[0];
};

const BankTransactions = () => {
  const [accounts, setAccounts] = useState([]);
  const [accountId, setAccountId] = useState('');
  const [start, setStart] = useState(daysAgo(60));
  const [end, setEnd] = useState(today());
  const [statusFilter, setStatusFilter] = useState('all');
  const [directionFilter, setDirectionFilter] = useState('all');
  const [search, setSearch] = useState('');

  const [transactions, setTransactions] = useState([]);
  const [recon, setRecon] = useState(null);
  const [loading, setLoading] = useState(false);

  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  const [tagOpen, setTagOpen] = useState(false);
  const [tagTxn, setTagTxn] = useState(null);
  const [tagSuggestions, setTagSuggestions] = useState([]);
  const [tagBelow, setTagBelow] = useState(false);
  const [tagLoading, setTagLoading] = useState(false);
  const [tagMode, setTagMode] = useState('match'); // 'match' | 'adjustment'
  const [adjCategory, setAdjCategory] = useState('bank_charge');
  const [adjDescription, setAdjDescription] = useState('');
  const [tagSubmitting, setTagSubmitting] = useState(false);

  // FY opening balance dialog
  const [openingOpen, setOpeningOpen] = useState(false);
  const [openingAmount, setOpeningAmount] = useState('');
  const [openingFy, setOpeningFy] = useState('');
  const [openingNote, setOpeningNote] = useState('');
  const [savingOpening, setSavingOpening] = useState(false);

  // Variance investigation dialog
  const [reasonOpen, setReasonOpen] = useState(false);
  const [reasonText, setReasonText] = useState('');
  const [savingReason, setSavingReason] = useState(false);

  // Manual add / edit dialog (shared)
  const [entryOpen, setEntryOpen] = useState(false);
  const [entryMode, setEntryMode] = useState('add'); // 'add' | 'edit'
  const [entryTxn, setEntryTxn] = useState(null);    // the source row when editing
  const [entryForm, setEntryForm] = useState({
    txn_date: today(),
    description: '',
    reference: '',
    direction: 'debit', // radio: 'debit' | 'credit'
    amount: '',
    statement_balance: '',
  });
  const [entrySaving, setEntrySaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  // Bulk-select (row checkboxes)
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);

  // Audit history drawer
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyTxn, setHistoryTxn] = useState(null);
  const [historyRows, setHistoryRows] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  // ---------- fetchers ----------
  const fetchAccounts = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/finance/accounts?type=BANK`, { withCredentials: true });
      const list = Array.isArray(res.data) ? res.data : [];
      setAccounts(list);
      if (list.length && !accountId) setAccountId(list[0].id);
    } catch {
      toast.error('Failed to load bank accounts');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchAll = useCallback(async () => {
    if (!accountId) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({ account_id: accountId });
      if (start) params.append('start', start);
      if (end) params.append('end', end);
      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (directionFilter !== 'all') params.append('direction', directionFilter);

      const [txRes, reconRes] = await Promise.all([
        axios.get(`${API}/bank/transactions?${params.toString()}`, { withCredentials: true }),
        axios.get(
          `${API}/bank/reconciliation?account_id=${accountId}&start=${start}&end=${end}`,
          { withCredentials: true }
        ),
      ]);
      setTransactions(txRes.data || []);
      setRecon(reconRes.data || null);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load bank transactions');
    } finally {
      setLoading(false);
    }
  }, [accountId, start, end, statusFilter, directionFilter]);

  useEffect(() => {
    fetchAccounts();
  }, [fetchAccounts]);
  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  // ---------- FY Opening Balance ----------
  // Compute the Indian FY code for an ISO date (Apr–Mar window).
  const fyCodeFor = (isoDate) => {
    const d = isoDate ? new Date(isoDate) : new Date();
    const y = d.getFullYear();
    const m = d.getMonth() + 1; // 1-12
    const [a, b] = m >= 4 ? [y, y + 1] : [y - 1, y];
    return `${String(a % 100).padStart(2, '0')}-${String(b % 100).padStart(2, '0')}`;
  };

  const openOpeningDialog = () => {
    if (!accountId) {
      toast.error('Pick an account first');
      return;
    }
    // Default the FY to the one covering the period start date
    setOpeningFy(fyCodeFor(start));
    setOpeningAmount(
      recon && recon.opening_source === 'fy_opening' ? String(recon.opening_balance) : ''
    );
    setOpeningNote(recon?.opening_note || '');
    setOpeningOpen(true);
  };

  const handleSaveOpening = async () => {
    const amt = parseFloat(openingAmount);
    if (isNaN(amt)) {
      toast.error('Enter a valid amount');
      return;
    }
    if (!openingFy || !/^\d{2}-\d{2}$/.test(openingFy)) {
      toast.error('FY code must be like "26-27"');
      return;
    }
    setSavingOpening(true);
    try {
      await axios.put(
        `${API}/bank/openings`,
        {
          account_id: accountId,
          fy_code: openingFy,
          amount: amt,
          note: openingNote || null,
        },
        { withCredentials: true }
      );
      toast.success('Opening balance saved');
      setOpeningOpen(false);
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally {
      setSavingOpening(false);
    }
  };

  // ---------- Variance Investigation ----------
  const openReasonDialog = () => {
    setReasonText('');
    setReasonOpen(true);
  };

  const handleSaveReason = async () => {
    if (!reasonText.trim()) {
      toast.error('Please describe the variance reason');
      return;
    }
    setSavingReason(true);
    try {
      await axios.post(
        `${API}/bank/recon-log`,
        {
          account_id: accountId,
          period_start: `${start}T00:00:00Z`,
          period_end: `${end}T23:59:59Z`,
          reason: reasonText.trim(),
          variance_amount: recon?.variance ?? null,
        },
        { withCredentials: true }
      );
      toast.success('Variance reason logged');
      setReasonOpen(false);
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally {
      setSavingReason(false);
    }
  };

  // ---------- Upload ----------
  const handleDownloadTemplate = async () => {
    try {
      const res = await axios.get(`${API}/bank/transactions/template`, {
        withCredentials: true,
        responseType: 'blob',
      });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'bank_statement_template.csv';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Failed to download template');
    }
  };

  const handleUpload = async () => {
    if (!uploadFile) {
      toast.error('Choose a CSV file first');
      return;
    }
    if (!accountId) {
      toast.error('Pick a bank account first');
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', uploadFile);
      const res = await axios.post(
        `${API}/bank/transactions/upload?account_id=${accountId}`,
        fd,
        { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } }
      );
      toast.success(
        `Imported ${res.data.rows_new} new · ${res.data.rows_skipped} skipped (duplicates) of ${res.data.rows_total} rows`
      );
      if ((res.data.errors || []).length) {
        console.warn('Bank import row errors:', res.data.errors);
      }
      setUploadOpen(false);
      setUploadFile(null);
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  // ---------- Manual add / edit / delete ----------
  const openAddDialog = () => {
    if (!accountId) {
      toast.error('Pick a bank account first');
      return;
    }
    setEntryMode('add');
    setEntryTxn(null);
    setEntryForm({
      txn_date: today(),
      description: '',
      reference: '',
      direction: 'debit',
      amount: '',
      statement_balance: '',
    });
    setEntryOpen(true);
  };

  const openEditDialog = (txn) => {
    if (txn.tagged_to_kind) {
      toast.error('Untag first — a linked payment/receipt is holding onto this row.');
      return;
    }
    setEntryMode('edit');
    setEntryTxn(txn);
    const isDebit = Number(txn.debit || 0) > 0;
    setEntryForm({
      txn_date: (txn.txn_date || '').slice(0, 10),
      description: txn.description || '',
      reference: txn.reference || '',
      direction: isDebit ? 'debit' : 'credit',
      amount: String(isDebit ? txn.debit : txn.credit),
      statement_balance: txn.statement_balance == null ? '' : String(txn.statement_balance),
    });
    setEntryOpen(true);
  };

  const handleEntrySubmit = async () => {
    const amount = Number(entryForm.amount);
    if (!entryForm.txn_date) return toast.error('Pick a date');
    if (!entryForm.description.trim()) return toast.error('Description is required');
    if (!(amount > 0)) return toast.error('Amount must be greater than 0');

    const debit = entryForm.direction === 'debit' ? amount : 0;
    const credit = entryForm.direction === 'credit' ? amount : 0;
    const stmt = entryForm.statement_balance === '' ? null : Number(entryForm.statement_balance);

    setEntrySaving(true);
    try {
      if (entryMode === 'add') {
        await axios.post(
          `${API}/bank/transactions`,
          {
            account_id: accountId,
            txn_date: entryForm.txn_date,
            description: entryForm.description.trim(),
            reference: entryForm.reference.trim() || null,
            debit,
            credit,
            statement_balance: stmt,
          },
          { withCredentials: true }
        );
        toast.success('Transaction added');
      } else {
        await axios.patch(
          `${API}/bank/transactions/${entryTxn.id}`,
          {
            txn_date: entryForm.txn_date,
            description: entryForm.description.trim(),
            reference: entryForm.reference.trim() || null,
            debit,
            credit,
            statement_balance: stmt,
          },
          { withCredentials: true }
        );
        toast.success('Transaction updated');
      }
      setEntryOpen(false);
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally {
      setEntrySaving(false);
    }
  };

  const handleDeleteTxn = async (txn) => {
    if (txn.tagged_to_kind) {
      toast.error('Untag first — a linked payment/receipt is holding onto this row.');
      return;
    }
    if (!window.confirm(`Delete this transaction (${formatDate(txn.txn_date)} · ${txn.description})?\nThis cannot be undone.`)) return;
    setDeletingId(txn.id);
    try {
      await axios.delete(`${API}/bank/transactions/${txn.id}`, { withCredentials: true });
      toast.success('Transaction deleted');
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    } finally {
      setDeletingId(null);
    }
  };

  // ---------- Bulk delete ----------
  const toggleRow = (id, checked) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });
  };
  const clearSelection = () => setSelectedIds(new Set());

  const handleBulkDelete = async () => {
    const ids = Array.from(selectedIds);
    if (!ids.length) return;
    if (!window.confirm(`Delete ${ids.length} selected transaction${ids.length > 1 ? 's' : ''}? Tagged rows will be skipped. This cannot be undone.`)) return;
    setBulkDeleting(true);
    try {
      const res = await axios.post(
        `${API}/bank/transactions/bulk-delete`,
        { ids },
        { withCredentials: true }
      );
      const { deleted = 0, blocked = [], missing = [] } = res.data || {};
      const parts = [`${deleted} deleted`];
      if (blocked.length) parts.push(`${blocked.length} skipped (tagged)`);
      if (missing.length) parts.push(`${missing.length} not found`);
      if (deleted && !blocked.length && !missing.length) toast.success(parts.join(' · '));
      else if (deleted) toast.warning(parts.join(' · '));
      else toast.error(parts.join(' · '));
      clearSelection();
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Bulk delete failed');
    } finally {
      setBulkDeleting(false);
    }
  };

  // ---------- Audit history drawer ----------
  const openHistory = async (txn) => {
    setHistoryTxn(txn);
    setHistoryOpen(true);
    setHistoryRows([]);
    setHistoryLoading(true);
    try {
      const res = await axios.get(`${API}/bank/transactions/${txn.id}/audit`, {
        withCredentials: true,
      });
      setHistoryRows(res.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load history');
    } finally {
      setHistoryLoading(false);
    }
  };

  // ---------- Tag dialog ----------
  const openTagDialog = async (txn) => {
    setTagTxn(txn);
    setTagOpen(true);
    setTagMode('match');
    setAdjCategory('bank_charge');
    setAdjDescription('');
    setTagLoading(true);
    try {
      const res = await axios.get(`${API}/bank/transactions/${txn.id}/suggestions`, {
        withCredentials: true,
      });
      setTagSuggestions(res.data?.matches || []);
      setTagBelow(!!res.data?.below_threshold);
    } catch {
      setTagSuggestions([]);
      setTagBelow(true);
    } finally {
      setTagLoading(false);
    }
  };

  const submitTagMatch = async (kind, target_id) => {
    setTagSubmitting(true);
    try {
      await axios.post(
        `${API}/bank/transactions/${tagTxn.id}/tag`,
        { kind, target_id },
        { withCredentials: true }
      );
      toast.success('Tagged successfully');
      setTagOpen(false);
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Tagging failed');
    } finally {
      setTagSubmitting(false);
    }
  };

  const submitTagAdjustment = async () => {
    if (!adjCategory) return;
    setTagSubmitting(true);
    try {
      await axios.post(
        `${API}/bank/transactions/${tagTxn.id}/tag`,
        {
          kind: 'adjustment',
          adjustment_category: adjCategory,
          adjustment_description: adjDescription,
        },
        { withCredentials: true }
      );
      toast.success('Marked as bank adjustment');
      setTagOpen(false);
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Tagging failed');
    } finally {
      setTagSubmitting(false);
    }
  };

  const handleUntag = async (txn) => {
    if (!window.confirm('Untag this transaction? The link to the manual payment/receipt or adjustment will be removed.')) return;
    try {
      await axios.post(
        `${API}/bank/transactions/${txn.id}/untag`,
        {},
        { withCredentials: true }
      );
      toast.success('Untagged');
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Untag failed');
    }
  };

  // ---------- derived ----------
  const filteredTxns = useMemo(() => {
    if (!search.trim()) return transactions;
    const s = search.trim().toLowerCase();
    return transactions.filter(
      (t) =>
        (t.description || '').toLowerCase().includes(s) ||
        (t.reference || '').toLowerCase().includes(s)
    );
  }, [transactions, search]);

  // Selectable = untagged rows in the current filter. Anything tagged is
  // ineligible for bulk delete (backend would reject anyway).
  const selectableIds = useMemo(
    () => filteredTxns.filter((t) => !t.tagged_to_kind).map((t) => t.id),
    [filteredTxns]
  );
  const allSelected =
    selectableIds.length > 0 && selectableIds.every((id) => selectedIds.has(id));
  const someSelected = selectedIds.size > 0 && !allSelected;
  const toggleSelectAll = (checked) => {
    setSelectedIds(checked ? new Set(selectableIds) : new Set());
  };
  // Purge selected ids that no longer exist in the latest fetch.
  useEffect(() => {
    setSelectedIds((prev) => {
      const validIds = new Set(transactions.map((t) => t.id));
      const next = new Set();
      prev.forEach((id) => { if (validIds.has(id)) next.add(id); });
      return next.size === prev.size ? prev : next;
    });
  }, [transactions]);

  const accountName = (id) => accounts.find((a) => a.id === id)?.name || '—';

  // ─────────────────── render ───────────────────
  return (
    <div className="space-y-4" data-testid="bank-transactions-tab">
      {/* Toolbar */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="font-heading flex items-center gap-2">
            Bank Transactions
          </CardTitle>
          <CardDescription>
            Upload your bank statement as CSV, then tag each line to a manual Payment or Receipt
            already in the system. Untagged lines surface here so you don't miss a transaction.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex flex-wrap items-end gap-3">
            <div className="space-y-1">
              <Label className="text-xs">Account</Label>
              <Select value={accountId} onValueChange={setAccountId}>
                <SelectTrigger className="w-56" data-testid="bank-tx-account">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {accounts.map((a) => (
                    <SelectItem key={a.id} value={a.id}>
                      {a.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">From</Label>
              <Input type="date" value={start} onChange={(e) => setStart(e.target.value)} className="w-40" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">To</Label>
              <Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="w-40" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Status</Label>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-36">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="untagged">Untagged</SelectItem>
                  <SelectItem value="tagged">Tagged</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Direction</Label>
              <Select value={directionFilter} onValueChange={setDirectionFilter}>
                <SelectTrigger className="w-36">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="debit">Debit (Out)</SelectItem>
                  <SelectItem value="credit">Credit (In)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1 flex-1 min-w-[180px]">
              <Label className="text-xs">Search</Label>
              <div className="relative">
                <Search className="h-3.5 w-3.5 absolute left-2 top-2.5 text-muted-foreground" />
                <Input
                  className="pl-7"
                  placeholder="Description / reference"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={handleDownloadTemplate} data-testid="bank-tx-template-btn">
                <Download className="h-4 w-4 mr-1.5" /> Template
              </Button>
              <Button variant="outline" onClick={openAddDialog} data-testid="bank-tx-add-btn" disabled={!accountId}>
                <Plus className="h-4 w-4 mr-1.5" /> Add transaction
              </Button>
              <Button onClick={() => setUploadOpen(true)} data-testid="bank-tx-upload-btn">
                <Upload className="h-4 w-4 mr-1.5" /> Upload statement
              </Button>
              <Button variant="ghost" size="icon" onClick={fetchAll} title="Refresh">
                <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Reconciliation tiles */}
      {recon && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3" data-testid="bank-tx-recon">
          <Tile
            label={
              <span className="flex items-center justify-between gap-2">
                <span>Opening</span>
                <button
                  type="button"
                  onClick={openOpeningDialog}
                  className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-primary hover:underline"
                  data-testid="set-opening-btn"
                  title="Set or update the opening balance for this Financial Year"
                >
                  <Pencil className="h-3 w-3" />
                  {recon.opening_source === 'fy_opening' ? 'Edit' : 'Set'}
                </button>
              </span>
            }
            value={formatINR(recon.opening_balance)}
            sub={
              recon.opening_source === 'fy_opening'
                ? `FY ${recon.opening_fy}${recon.opening_note ? ' · ' + recon.opening_note : ''}`
                : recon.opening_date
                ? `as of ${formatDate(recon.opening_date)}`
                : 'no prior balance · click Set'
            }
          />
          <Tile label="Computed Close" value={formatINR(recon.computed_close)} sub={`+₹${recon.sum_credits.toLocaleString('en-IN')} − ₹${recon.sum_debits.toLocaleString('en-IN')}`} />
          <Tile label="Actual Close" value={formatINR(recon.actual_close)} sub={recon.actual_date ? `from Bank Balance · ${formatDate(recon.actual_date)}` : 'no Bank Balance entry'} />
          <Tile
            label={
              <span className="flex items-center justify-between gap-2">
                <span>Variance</span>
                {!recon.is_reconciled && (
                  <button
                    type="button"
                    onClick={openReasonDialog}
                    className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-primary hover:underline"
                    data-testid="investigate-variance-btn"
                    title="Record the reason for this variance"
                  >
                    <FileText className="h-3 w-3" />
                    Investigate
                  </button>
                )}
              </span>
            }
            value={formatINR(recon.variance)}
            sub={recon.is_reconciled ? 'Reconciled ✓' : 'Mismatch — investigate'}
            valueClass={recon.is_reconciled ? 'text-emerald-700' : 'text-destructive'}
            icon={recon.is_reconciled ? CheckCircle2 : AlertTriangle}
          />
        </div>
      )}

      {/* Variance investigation log — newest first, scrollable. Surfaces what
          accountants have already recorded so they don't double-log the
          same explanation. */}
      {recon?.recon_notes?.length > 0 && (
        <Card className="border-amber-200 bg-amber-50/40" data-testid="recon-log-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <HistoryIcon className="h-4 w-4" />
              Variance Investigation Notes ({recon.recon_notes.length})
            </CardTitle>
            <CardDescription className="text-xs">
              History of reasons recorded for this account & period.
            </CardDescription>
          </CardHeader>
          <CardContent className="pt-0">
            <ul className="space-y-2 max-h-48 overflow-auto text-xs">
              {recon.recon_notes.map((n) => (
                <li key={n.id || n.created_at} className="border-l-2 border-amber-400 pl-2">
                  <div className="text-muted-foreground">
                    {formatDate(n.created_at)} {String(n.created_at || '').slice(11, 16)} · {n.created_by}
                    {n.variance_amount != null && (
                      <> · variance was <strong>{formatINR(n.variance_amount)}</strong></>
                    )}
                  </div>
                  <div className="whitespace-pre-wrap">{n.reason}</div>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      {/* Untagged summary */}
      {recon && (recon.untagged_summary?.debit_count + recon.untagged_summary?.credit_count > 0) && (
        <Card className="border-amber-300 bg-amber-50/60">
          <CardContent className="py-3 flex flex-wrap items-center gap-x-6 gap-y-1 text-sm">
            <div className="flex items-center gap-2 text-amber-800">
              <AlertTriangle className="h-4 w-4" />
              <span className="font-medium">Untagged transactions in this period</span>
            </div>
            <button
              type="button"
              className="hover:underline"
              onClick={() => {
                setStatusFilter('untagged');
                setDirectionFilter('debit');
              }}
            >
              <span className="font-mono font-semibold">{recon.untagged_summary.debit_count}</span> debit ·{' '}
              <span className="font-mono">{formatINR(recon.untagged_summary.debit_total)}</span> (likely missing payment)
            </button>
            <button
              type="button"
              className="hover:underline"
              onClick={() => {
                setStatusFilter('untagged');
                setDirectionFilter('credit');
              }}
            >
              <span className="font-mono font-semibold">{recon.untagged_summary.credit_count}</span> credit ·{' '}
              <span className="font-mono">{formatINR(recon.untagged_summary.credit_total)}</span> (likely missing receipt)
            </button>
          </CardContent>
        </Card>
      )}

      {/* Bulk action bar — appears only when rows are selected */}
      {selectedIds.size > 0 && (
        <div
          className="flex items-center justify-between gap-3 rounded-lg border border-destructive/30 bg-destructive/[0.06] px-4 py-2.5"
          data-testid="bank-tx-bulk-bar"
        >
          <div className="text-sm">
            <span className="font-semibold" data-testid="bank-tx-bulk-count">
              {selectedIds.size}
            </span>{' '}
            selected · Tagged rows are skipped automatically.
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={clearSelection} disabled={bulkDeleting}>
              Clear
            </Button>
            <Button
              variant="destructive"
              size="sm"
              onClick={handleBulkDelete}
              disabled={bulkDeleting}
              data-testid="bank-tx-bulk-delete-btn"
            >
              <Trash2 className="h-3.5 w-3.5 mr-1.5" />
              {bulkDeleting ? 'Deleting…' : `Delete ${selectedIds.size} selected`}
            </Button>
          </div>
        </div>
      )}

      {/* Transactions table */}
      <Card>
        <CardContent className="p-0">
          {filteredTxns.length === 0 ? (
            <div className="py-12 text-center text-sm text-muted-foreground">
              No transactions in this period. Upload a statement to begin.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted/40 text-xs uppercase tracking-wider text-muted-foreground">
                  <tr>
                    <th className="p-2 pl-3 w-8">
                      <Checkbox
                        checked={allSelected ? true : someSelected ? 'indeterminate' : false}
                        onCheckedChange={(v) => toggleSelectAll(!!v)}
                        disabled={selectableIds.length === 0}
                        aria-label="Select all untagged rows"
                        data-testid="bank-tx-select-all"
                      />
                    </th>
                    <th className="text-left p-2">Date</th>
                    <th className="text-left p-2">Description</th>
                    <th className="text-left p-2">Ref</th>
                    <th className="text-right p-2">Debit</th>
                    <th className="text-right p-2">Credit</th>
                    <th className="text-left p-2">Status</th>
                    <th className="text-right p-2 pr-3">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredTxns.map((t) => (
                    <tr
                      key={t.id}
                      className={`border-t hover:bg-muted/20 ${selectedIds.has(t.id) ? 'bg-destructive/[0.03]' : ''}`}
                      data-testid={`bank-tx-row-${t.id}`}
                    >
                      <td className="p-2 pl-3 align-middle">
                        <Checkbox
                          checked={selectedIds.has(t.id)}
                          onCheckedChange={(v) => toggleRow(t.id, !!v)}
                          disabled={!!t.tagged_to_kind}
                          aria-label={t.tagged_to_kind ? 'Row is tagged and cannot be bulk-deleted' : 'Select this row'}
                          data-testid={`bank-tx-select-${t.id}`}
                        />
                      </td>
                      <td className="p-2 whitespace-nowrap font-mono text-xs">{formatDate(t.txn_date)}</td>
                      <td className="p-2 max-w-[28ch] truncate" title={t.description}>{t.description}</td>
                      <td className="p-2 font-mono text-xs text-muted-foreground">{t.reference || '—'}</td>
                      <td className="p-2 text-right font-mono text-destructive">
                        {t.debit > 0 ? formatINR(t.debit) : ''}
                      </td>
                      <td className="p-2 text-right font-mono text-emerald-700">
                        {t.credit > 0 ? formatINR(t.credit) : ''}
                      </td>
                      <td className="p-2">
                        {t.tagged_to_kind ? (
                          <Badge variant="outline" className="border-emerald-300 text-emerald-700 bg-emerald-50">
                            {t.tagged_to_kind}
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="border-amber-300 text-amber-700 bg-amber-50">
                            untagged
                          </Badge>
                        )}
                      </td>
                      <td className="p-2 pr-3 text-right">
                        <div className="inline-flex items-center gap-1">
                          {t.tagged_to_kind ? (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleUntag(t)}
                              data-testid={`bank-tx-untag-${t.id}`}
                            >
                              <X className="h-3.5 w-3.5 mr-1" /> Untag
                            </Button>
                          ) : (
                            <Button
                              size="sm"
                              variant="default"
                              onClick={() => openTagDialog(t)}
                              data-testid={`bank-tx-tag-${t.id}`}
                            >
                              <Tag className="h-3.5 w-3.5 mr-1" /> Tag
                            </Button>
                          )}
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-8 w-8"
                            onClick={() => openHistory(t)}
                            disabled={!t.updated_at}
                            title={t.updated_at ? `History · last edit ${formatDate(t.updated_at)}` : 'No edit history yet'}
                            data-testid={`bank-tx-history-${t.id}`}
                          >
                            <HistoryIcon className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-8 w-8"
                            onClick={() => openEditDialog(t)}
                            disabled={!!t.tagged_to_kind}
                            title={t.tagged_to_kind ? 'Untag first' : 'Edit transaction'}
                            data-testid={`bank-tx-edit-${t.id}`}
                          >
                            <Pencil className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-8 w-8 text-destructive hover:text-destructive hover:bg-destructive/10"
                            onClick={() => handleDeleteTxn(t)}
                            disabled={!!t.tagged_to_kind || deletingId === t.id}
                            title={t.tagged_to_kind ? 'Untag first' : 'Delete transaction'}
                            data-testid={`bank-tx-delete-${t.id}`}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add / Edit transaction dialog */}
      <Dialog open={entryOpen} onOpenChange={setEntryOpen}>
        <DialogContent className="max-w-md" data-testid="bank-tx-entry-dialog">
          <DialogHeader>
            <DialogTitle>
              {entryMode === 'add' ? 'Add bank transaction' : 'Edit bank transaction'}
            </DialogTitle>
            <DialogDescription>
              {entryMode === 'add'
                ? <>Manually enter one row. Same de-duplication rules as CSV upload apply. Account: <span className="font-medium text-foreground">{accountName(accountId)}</span>.</>
                : 'Correct a row that was imported or manually added. Tagged rows must be untagged first.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>Date</Label>
                <Input
                  type="date"
                  value={entryForm.txn_date}
                  onChange={(e) => setEntryForm((f) => ({ ...f, txn_date: e.target.value }))}
                  data-testid="bank-tx-entry-date"
                />
              </div>
              <div className="space-y-1">
                <Label>Direction</Label>
                <Select
                  value={entryForm.direction}
                  onValueChange={(v) => setEntryForm((f) => ({ ...f, direction: v }))}
                >
                  <SelectTrigger data-testid="bank-tx-entry-direction">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="debit">Debit (Out)</SelectItem>
                    <SelectItem value="credit">Credit (In)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1">
              <Label>Description</Label>
              <Input
                placeholder="e.g. Bank charges Aug-26 / NEFT-VENDOR ABC"
                value={entryForm.description}
                onChange={(e) => setEntryForm((f) => ({ ...f, description: e.target.value }))}
                data-testid="bank-tx-entry-description"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>Reference / UTR</Label>
                <Input
                  placeholder="Optional"
                  value={entryForm.reference}
                  onChange={(e) => setEntryForm((f) => ({ ...f, reference: e.target.value }))}
                  data-testid="bank-tx-entry-reference"
                />
              </div>
              <div className="space-y-1">
                <Label>Amount (₹)</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="0.00"
                  value={entryForm.amount}
                  onChange={(e) => setEntryForm((f) => ({ ...f, amount: e.target.value }))}
                  data-testid="bank-tx-entry-amount"
                />
              </div>
            </div>
            <div className="space-y-1">
              <Label>Statement balance <span className="text-muted-foreground text-xs">(optional)</span></Label>
              <Input
                type="number"
                step="0.01"
                placeholder="Balance printed on the statement, if any"
                value={entryForm.statement_balance}
                onChange={(e) => setEntryForm((f) => ({ ...f, statement_balance: e.target.value }))}
                data-testid="bank-tx-entry-balance"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEntryOpen(false)} disabled={entrySaving}>
              Cancel
            </Button>
            <Button onClick={handleEntrySubmit} disabled={entrySaving} data-testid="bank-tx-entry-submit">
              {entrySaving ? 'Saving…' : entryMode === 'add' ? 'Add transaction' : 'Save changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit-history drawer for a single transaction */}
      <Dialog open={historyOpen} onOpenChange={setHistoryOpen}>
        <DialogContent className="max-w-lg" data-testid="bank-tx-history-dialog">
          <DialogHeader>
            <DialogTitle>Edit history</DialogTitle>
            <DialogDescription>
              {historyTxn ? (
                <>
                  {formatDate(historyTxn.txn_date)} · <span className="font-medium text-foreground">{historyTxn.description}</span>
                </>
              ) : ''}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-1">
            {historyLoading ? (
              <div className="py-6 text-center text-sm text-muted-foreground">Loading history…</div>
            ) : historyRows.length === 0 ? (
              <div className="py-6 text-center text-sm text-muted-foreground">
                No edits recorded. Every future edit will appear here with the before/after values.
              </div>
            ) : (
              historyRows.map((row) => (
                <div key={row.id} className="rounded-md border bg-muted/20 p-3" data-testid={`bank-tx-history-row-${row.id}`}>
                  <div className="flex items-center justify-between text-xs mb-2">
                    <span className="font-mono text-muted-foreground">
                      {(row.at || '').slice(0, 19).replace('T', ' ')} UTC
                    </span>
                    <span className="font-medium">{row.by || '—'}</span>
                  </div>
                  <ul className="space-y-1.5 text-sm">
                    {Object.entries(row.changes || {}).map(([field, delta]) => (
                      <li key={field} className="grid grid-cols-[minmax(80px,auto)_1fr] gap-2 items-start">
                        <span className="text-xs uppercase tracking-wider text-muted-foreground pt-0.5">
                          {field.replace(/_/g, ' ')}
                        </span>
                        <div className="min-w-0">
                          <span className="line-through text-muted-foreground break-all">
                            {formatAuditValue(field, delta.from)}
                          </span>
                          <span className="mx-2 text-muted-foreground">→</span>
                          <span className="font-medium break-all">
                            {formatAuditValue(field, delta.to)}
                          </span>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              ))
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setHistoryOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Upload dialog */}
      <Dialog open={uploadOpen} onOpenChange={setUploadOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Upload bank statement</DialogTitle>
            <DialogDescription>
              CSV with columns: <span className="font-mono">date, description, reference, debit, credit, balance</span>.
              Re-uploading the same file is a no-op (duplicates are skipped).
              Account: <span className="font-medium text-foreground">{accountName(accountId)}</span>.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>CSV file</Label>
              <Input
                type="file"
                accept=".csv,text/csv"
                onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
                data-testid="bank-tx-file-input"
              />
            </div>
            <Button variant="link" className="px-0 h-auto" onClick={handleDownloadTemplate}>
              <Download className="h-3.5 w-3.5 mr-1" /> Download CSV template
            </Button>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setUploadOpen(false)} disabled={uploading}>
              Cancel
            </Button>
            <Button onClick={handleUpload} disabled={uploading || !uploadFile} data-testid="bank-tx-upload-submit">
              {uploading ? 'Uploading…' : 'Upload'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Tag dialog */}
      <Dialog open={tagOpen} onOpenChange={setTagOpen}>
        <DialogContent className="max-w-2xl" data-testid="bank-tx-tag-dialog">
          <DialogHeader>
            <DialogTitle>Tag bank transaction</DialogTitle>
            <DialogDescription>
              {tagTxn ? (
                <>
                  {formatDate(tagTxn.txn_date)} · {tagTxn.description} ·{' '}
                  <span className={tagTxn.debit > 0 ? 'text-destructive' : 'text-emerald-700'}>
                    {formatINR(tagTxn.debit > 0 ? tagTxn.debit : tagTxn.credit)} {tagTxn.debit > 0 ? 'DR' : 'CR'}
                  </span>
                </>
              ) : null}
            </DialogDescription>
          </DialogHeader>

          <div className="flex gap-2 border-b pb-2">
            <Button
              variant={tagMode === 'match' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setTagMode('match')}
            >
              Match to manual {tagTxn?.debit > 0 ? 'Payment' : 'Receipt'}
            </Button>
            <Button
              variant={tagMode === 'adjustment' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setTagMode('adjustment')}
            >
              Mark as bank adjustment
            </Button>
          </div>

          {tagMode === 'match' ? (
            <div className="space-y-2 max-h-[420px] overflow-y-auto">
              {tagLoading ? (
                <div className="py-8 text-center text-sm text-muted-foreground">Looking for matches…</div>
              ) : tagBelow ? (
                <div className="py-6 text-center text-sm">
                  <AlertTriangle className="h-5 w-5 mx-auto text-amber-600 mb-1" />
                  <div className="font-medium">No likely match found</div>
                  <div className="text-xs text-muted-foreground mt-1">
                    Either the {tagTxn?.debit > 0 ? 'payment' : 'receipt'} hasn't been entered manually yet,
                    or this is a bank-only line. Add it in the {tagTxn?.debit > 0 ? 'Payments' : 'Revenue → Receipts'} module
                    first, then return here to tag — or use "Mark as bank adjustment" if it's a charge / interest.
                  </div>
                </div>
              ) : (
                tagSuggestions.map((m) => {
                  const c = m.candidate;
                  const isPayment = m.kind === 'payment';
                  return (
                    <div
                      key={c.id}
                      className="border rounded-md p-3 flex items-start justify-between gap-3 hover:border-primary/40 transition-colors"
                    >
                      <div className="min-w-0">
                        <div className="font-medium text-sm">
                          {isPayment ? c.party_name : c.invoice_number || c.receipt_number}
                          <Badge variant="outline" className="ml-2 text-[10px]">
                            score {m.score}
                          </Badge>
                        </div>
                        <div className="text-xs text-muted-foreground mt-0.5">
                          {formatDate(isPayment ? c.date : c.receipt_date)} · {formatINR(c.amount)} ·{' '}
                          {c.reference_number || (isPayment ? c.narration : c.remarks) || '—'}
                        </div>
                      </div>
                      <Button
                        size="sm"
                        onClick={() => submitTagMatch(m.kind, c.id)}
                        disabled={tagSubmitting}
                        data-testid={`bank-tx-pick-${c.id}`}
                      >
                        Tag this
                      </Button>
                    </div>
                  );
                })
              )}
            </div>
          ) : (
            <div className="space-y-3">
              <div>
                <Label>Category</Label>
                <Select value={adjCategory} onValueChange={setAdjCategory}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {ADJ_CATEGORIES.map((c) => (
                      <SelectItem key={c.v} value={c.v}>{c.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Description (optional)</Label>
                <Textarea
                  rows={2}
                  value={adjDescription}
                  onChange={(e) => setAdjDescription(e.target.value)}
                  placeholder={tagTxn?.description}
                />
              </div>
              <div className="text-xs text-muted-foreground">
                Use this for bank-only lines (charges, interest, internal transfers) that don't have
                a corresponding manual Payment / Receipt in the tool.
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setTagOpen(false)} disabled={tagSubmitting}>
              Cancel
            </Button>
            {tagMode === 'adjustment' && (
              <Button onClick={submitTagAdjustment} disabled={tagSubmitting} data-testid="bank-tx-adj-save">
                {tagSubmitting ? 'Saving…' : 'Save adjustment'}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Set FY opening balance */}
      <Dialog open={openingOpen} onOpenChange={setOpeningOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Set Opening Balance</DialogTitle>
            <DialogDescription>
              One opening balance per account per Financial Year. The reconciliation
              automatically rolls this into the computed close.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Financial Year (Apr–Mar)</Label>
              <Input
                value={openingFy}
                onChange={(e) => setOpeningFy(e.target.value.trim())}
                placeholder="e.g. 26-27"
                data-testid="opening-fy-input"
              />
              <p className="text-[11px] text-muted-foreground mt-1">
                Use the format <strong>YY-YY</strong>. The default reflects the period start above.
              </p>
            </div>
            <div>
              <Label className="text-xs">Amount (₹)</Label>
              <Input
                type="number"
                step="0.01"
                value={openingAmount}
                onChange={(e) => setOpeningAmount(e.target.value)}
                placeholder="0.00"
                data-testid="opening-amount-input"
                className="font-mono"
              />
            </div>
            <div>
              <Label className="text-xs">Source / Note (optional)</Label>
              <Input
                value={openingNote}
                onChange={(e) => setOpeningNote(e.target.value)}
                placeholder="e.g. Carried forward from FY 25-26"
                data-testid="opening-note-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpeningOpen(false)} disabled={savingOpening}>
              Cancel
            </Button>
            <Button
              onClick={handleSaveOpening}
              disabled={savingOpening}
              data-testid="opening-save-btn"
            >
              {savingOpening ? 'Saving…' : 'Save Opening'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Investigate variance — long-form reason */}
      <Dialog open={reasonOpen} onOpenChange={setReasonOpen}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>Investigate Variance</DialogTitle>
            <DialogDescription>
              Current variance:{' '}
              <strong className="text-destructive">
                {recon ? formatINR(recon.variance) : '—'}
              </strong>
              . Describe what's causing the difference in detail. This will be added to the
              audit trail and visible to the team.
            </DialogDescription>
          </DialogHeader>
          <Textarea
            value={reasonText}
            onChange={(e) => setReasonText(e.target.value)}
            rows={8}
            placeholder={
              'Examples:\n• Bank interest of ₹2,000 credited on 31-Mar-2026 not yet entered as a receipt.\n• Quarterly bank charges of ₹500 not entered as a payment.\n• Cheque #1234 issued 28-Mar-2026 for ₹15,000 still in transit (cleared next month).\n• Receipt against Invoice INV-2026/045 received in cash on 02-Apr-2026 but recorded as 31-Mar.'
            }
            data-testid="recon-reason-input"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setReasonOpen(false)} disabled={savingReason}>
              Cancel
            </Button>
            <Button
              onClick={handleSaveReason}
              disabled={savingReason || !reasonText.trim()}
              data-testid="recon-reason-save-btn"
            >
              {savingReason ? 'Saving…' : 'Save Reason'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const Tile = ({ label, value, sub, valueClass = '', icon: Icon }) => (
  <div className="border rounded-md p-3 bg-card">
    <div className="text-xs uppercase tracking-wider text-muted-foreground flex items-center gap-1">
      {Icon && <Icon className="h-3 w-3" />} {label}
    </div>
    <div className={`text-2xl font-bold font-mono mt-1 ${valueClass}`}>{value}</div>
    {sub && <div className="text-[11px] text-muted-foreground mt-0.5">{sub}</div>}
  </div>
);

export default BankTransactions;
